/**
 * Column Visibility Cache Service
 *
 * Generic service for managing column visibility preferences in localStorage.
 * Can be reused across different modules by providing a unique storage key.
 *
 * @example
 * ```typescript
 * // For cases module
 * const casesCache = new ColumnVisibilityCacheService('case_list_visible_columns');
 * const visibleColumns = casesCache.getVisibleColumns();
 * casesCache.saveVisibleColumns(new Set(['id', 'name', 'status']));
 *
 * // For queue module
 * const queueCache = new ColumnVisibilityCacheService('queue_list_visible_columns');
 * ```
 */

// ============================================================================
// Types
// ============================================================================

export interface ColumnVisibilityCache {
  /**
   * Array of column keys that should be visible
   * (stored as array for JSON serialization)
   */
  visibleColumns: string[];
  /**
   * Array of column keys in the preferred order
   * (stored as array for JSON serialization)
   */
  columnOrder?: string[];
  /**
   * Timestamp when cache was last updated
   */
  timestamp: number;
}

export interface ColumnWithMetadata {
  /**
   * Unique key for the column
   */
  key: string;
  /**
   * Whether the column is visible by default
   */
  isVisibleDefault?: boolean;
  /**
   * Whether the column can be shown (permission check)
   */
  canShow?: boolean;
}

// ============================================================================
// Column Visibility Cache Service Class
// ============================================================================

export class ColumnVisibilityCacheService {
  private readonly storageKey: string;

  /**
   * Create a new column visibility cache service instance
   * @param storageKey - Unique localStorage key for this cache instance
   */
  constructor(storageKey: string) {
    if (!storageKey || storageKey.trim() === '') {
      throw new Error('Storage key is required');
    }
    this.storageKey = storageKey;
  }

  // --------------------------------------------------------------------------
  // Get Operations
  // --------------------------------------------------------------------------

  /**
   * Get cached visible columns from localStorage
   * @returns Set of visible column keys, or null if no cache exists
   */
  getVisibleColumns(): Set<string> | null {
    const parsed = this.readCache();
    if (!parsed) return null;

    // Convert array back to Set
    return new Set<string>(parsed.visibleColumns);
  }

  /**
   * Get cached column order from localStorage
   * @returns Array of column keys in preferred order, or null if none exists
   */
  getColumnOrder(): string[] | null {
    const parsed = this.readCache();
    if (!parsed?.columnOrder || parsed.columnOrder.length === 0) {
      return null;
    }
    return parsed.columnOrder;
  }

  /**
   * Check if cache exists
   * @returns true if cache exists in localStorage
   */
  hasCache(): boolean {
    try {
      return localStorage.getItem(this.storageKey) !== null;
    } catch {
      return false;
    }
  }

  // --------------------------------------------------------------------------
  // Save Operations
  // --------------------------------------------------------------------------

  /**
   * Save visible columns to cache
   * @param visibleColumns - Set of column keys that should be visible
   */
  saveVisibleColumns(visibleColumns: Set<string>): void {
    const existing = this.readCache();
    const cache: ColumnVisibilityCache = {
      visibleColumns: Array.from(visibleColumns),
      ...(existing?.columnOrder ? { columnOrder: existing.columnOrder } : {}),
      timestamp: Date.now(),
    };
    this.writeCache(cache);
  }

  /**
   * Save column order to cache
   * @param columnOrder - Array of column keys in preferred order
   */
  saveColumnOrder(columnOrder: string[]): void {
    const existing = this.readCache();
    const cache: ColumnVisibilityCache = {
      visibleColumns: existing?.visibleColumns ?? [],
      columnOrder,
      timestamp: Date.now(),
    };
    this.writeCache(cache);
  }

  /**
   * Save both visibility and order to cache
   * @param visibleColumns - Set of column keys that should be visible
   * @param columnOrder - Array of column keys in preferred order
   */
  savePreferences(visibleColumns: Set<string>, columnOrder: string[]): void {
    const cache: ColumnVisibilityCache = {
      visibleColumns: Array.from(visibleColumns),
      columnOrder,
      timestamp: Date.now(),
    };
    this.writeCache(cache);
  }

  /**
   * Update visible columns (add or remove specific columns)
   * @param columns - Column keys to add or remove
   * @param add - If true, add columns; if false, remove columns
   */
  updateVisibleColumns(columns: string[], add: boolean): void {
    const current = this.getVisibleColumns() ?? new Set<string>();
    const updated = new Set(current);

    if (add) {
      columns.forEach(key => updated.add(key));
    } else {
      columns.forEach(key => updated.delete(key));
    }

    this.saveVisibleColumns(updated);
  }

  // --------------------------------------------------------------------------
  // Clear Operations
  // --------------------------------------------------------------------------

  /**
   * Clear the column visibility cache
   */
  clearCache(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error(`[ColumnVisibilityCache] Failed to clear cache for ${this.storageKey}:`, error);
    }
  }

  // --------------------------------------------------------------------------
  // Initialize Operations
  // --------------------------------------------------------------------------

  /**
   * Initialize visible columns from cache or use default
   * @param allColumns - All available columns with their metadata
   * @param defaultFilter - Function to determine default visibility
   * @returns Set of column keys that should be visible
   */
  initializeVisibleColumns<T extends ColumnWithMetadata>(allColumns: T[], defaultFilter: (col: T) => boolean): Set<string> {
    const cached = this.getVisibleColumns();

    if (cached) {
      // Validate cached columns against available columns
      const availableKeys = new Set(allColumns.map(col => col.key));
      const validCachedColumns = new Set<string>();

      // Only include cached columns that still exist in available columns
      for (const key of cached) {
        if (availableKeys.has(key)) {
          validCachedColumns.add(key);
        }
      }

      // If we have valid cached columns, use them
      if (validCachedColumns.size > 0) {
        return validCachedColumns;
      }
    }

    // Fall back to default visibility
    const defaultVisible = allColumns.filter(defaultFilter).map(col => col.key);

    const defaultSet = new Set(defaultVisible);

    // Cache the default selection
    this.saveVisibleColumns(defaultSet);

    return defaultSet;
  }

  /**
   * Get default visible columns based on filter function
   * @param allColumns - All available columns with their metadata
   * @param defaultFilter - Function to determine default visibility
   * @returns Set of column keys that should be visible by default
   */
  getDefaultVisibleColumns<T extends ColumnWithMetadata>(allColumns: T[], defaultFilter: (col: T) => boolean): Set<string> {
    const defaultVisible = allColumns.filter(defaultFilter).map(col => col.key);
    return new Set(defaultVisible);
  }

  // --------------------------------------------------------------------------
  // Utility Methods
  // --------------------------------------------------------------------------

  /**
   * Get cache metadata (timestamp)
   * @returns Cache timestamp or null if cache doesn't exist
   */
  getCacheTimestamp(): number | null {
    try {
      const cached = localStorage.getItem(this.storageKey);
      if (!cached) {
        return null;
      }

      const parsed: ColumnVisibilityCache = JSON.parse(cached);
      return parsed.timestamp;
    } catch {
      return null;
    }
  }

  // --------------------------------------------------------------------------
  // Internal Helpers
  // --------------------------------------------------------------------------

  private readCache(): ColumnVisibilityCache | null {
    try {
      const cached = localStorage.getItem(this.storageKey);
      if (!cached) {
        return null;
      }
      return JSON.parse(cached) as ColumnVisibilityCache;
    } catch (error) {
      console.error(`[ColumnVisibilityCache] Failed to read cache for ${this.storageKey}:`, error);
      return null;
    }
  }

  private writeCache(cache: ColumnVisibilityCache): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(cache));
    } catch (error) {
      console.error(`[ColumnVisibilityCache] Failed to save cache for ${this.storageKey}:`, error);
    }
  }

  /**
   * Get the storage key used by this service instance
   * @returns The storage key
   */
  getStorageKey(): string {
    return this.storageKey;
  }
}

// ============================================================================
// Pre-configured Singleton Instances
// ============================================================================

/**
 * Pre-configured cache service for case list columns
 */
export const caseListColumnCache = new ColumnVisibilityCacheService('case_list_visible_columns');

/**
 * Pre-configured cache service for user list columns
 */
export const userListColumnCache = new ColumnVisibilityCacheService('user_list_visible_columns');

/**
 * Pre-configured cache service for case tag list columns
 */
export const caseTagListColumnCache = new ColumnVisibilityCacheService('case_tag_list_visible_columns');

/**
 * Pre-configured cache service for call follow-up list columns
 */
export const callFollowUpListColumnCache = new ColumnVisibilityCacheService('call_follow_up_list_visible_columns');

/**
 * Pre-configured cache service for call history list columns
 */
export const callHistoryListColumnCache = new ColumnVisibilityCacheService('call_history_list_visible_columns');

/**
 * Pre-configured cache service for contact list columns
 */
export const contactListColumnCache = new ColumnVisibilityCacheService('contact_list_visible_columns');

/**
 * Pre-configured cache service for queue list columns
 */
export const queueListColumnCache = new ColumnVisibilityCacheService('queue_list_visible_columns');

/**
 * Pre-configured cache service for client list columns
 */
export const clientListColumnCache = new ColumnVisibilityCacheService('client_list_visible_columns');
